import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import './Customer.css';

interface Warranty {
  id: number;
  orderId: string;
  serialNumber: string;
  productImage: string;
  startDate: string;
  endDate: string;
  status: string;
  remainingDays: number;
  claimCount: number;
  latestClaimStatus: string;
  refundStatus: string;
  refundQrCode: string;
  refundAmount: number;
}

const CustomerDashboard: React.FC = () => {
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState<number | null>(null);
  const [refundQr, setRefundQr] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchWarranties();
  }, []);

  const fetchWarranties = async () => {
    try {
      const params = search ? { search } : {};
      const [warrantiesRes, creditsRes] = await Promise.all([
        axios.get('/api/warranty/dashboard', { params }),
        axios.get('/api/credit/user/balance')
      ]);
      setWarranties(warrantiesRes.data.warranties);
      setCreditBalance(creditsRes.data.balance);
    } catch (error) {
      console.error('Failed to fetch warranties:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Loading...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">📦 My Warranties</h1>
            <p style={{ marginTop: '5px', color: '#666' }}>
              Credit Balance: <strong style={{ color: creditBalance >= 0 ? '#28a745' : '#dc3545' }}>{creditBalance}</strong>
            </p>
            <div style={{ marginTop: '15px', maxWidth: '400px', display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="Search Order ID"
                className="form-control"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchWarranties()}
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-primary"
                onClick={() => fetchWarranties()}
                style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <span>🔍</span> Search
              </button>
            </div>
          </div>
          <Link to="/customer/activate-warranty" className="btn btn-primary">
            Activate New Warranty
          </Link>
        </div>

        {warranties.length === 0 ? (
          <div className="card">
            <p>No warranties found. Activate your first warranty to get started.</p>
          </div>
        ) : (
          <div className="warranties-grid">
            {warranties.map((warranty) => (
              <div key={warranty.id} className="card warranty-card">
                <div className="warranty-header">
                  <div>
                    <h3 style={{ color: '#667eea', fontWeight: 'bold' }}>{warranty.orderId}</h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>{warranty.serialNumber}</p>
                  </div>
                  <span className={`status-badge status-${warranty.status}`}>
                    {warranty.status.toUpperCase()}
                  </span>
                </div>
                {warranty.productImage && (
                  <img
                    src={warranty.productImage}
                    alt="Product"
                    className="product-image"
                  />
                )}
                <div className="warranty-info">
                  <p><strong>Start Date:</strong> {new Date(warranty.startDate).toLocaleDateString()}</p>
                  <p><strong>End Date:</strong> {new Date(warranty.endDate).toLocaleDateString()}</p>
                  <p><strong>Remaining Days:</strong> {warranty.remainingDays}</p>
                  {warranty.latestClaimStatus && warranty.latestClaimStatus !== 'none' && (
                    <p><strong>Claim Status:</strong> {warranty.latestClaimStatus}</p>
                  )}
                </div>
                <div className="warranty-actions">
                  {warranty.status === 'active' && (
                    <>
                      {warranty.claimCount === 0 && (
                        <Link
                          to={`/customer/submit-claim/${warranty.id}`}
                          className="btn btn-primary"
                        >
                          Submit Claim
                        </Link>
                      )}
                    </>
                  )}
                  {warranty.refundStatus === 'none' && (
                    <button
                      onClick={() => {
                        if (warranty.status === 'expired') {
                          setSelectedWarranty(warranty.id);
                          setShowRefundModal(true);
                        }
                      }}
                      className="btn btn-primary"
                      style={{
                        background: '#6c757d',
                        borderColor: '#6c757d',
                        opacity: warranty.status !== 'expired' ? 0.5 : 1,
                        cursor: warranty.status !== 'expired' ? 'not-allowed' : 'pointer'
                      }}
                      disabled={warranty.status !== 'expired'}
                      title={warranty.status !== 'expired' ? 'Available after warranty expires' : ''}
                    >
                      Get Refund Bonus
                    </button>
                  )}
                  {warranty.refundStatus === 'pending' && (
                    <span className="status-badge status-pending">Refund Pending</span>
                  )}
                  {warranty.refundStatus === 'approved' && (
                    <span className="status-badge status-done">DONE</span>
                  )}
                  {warranty.refundStatus === 'rejected' && (
                    <span className="status-badge status-rejected">Rejected</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div >

      {showRefundModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Get Refund Bonus</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundQr(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Upload your QR Code for refund (50% bonus).</p>
              <div
                className="file-input-wrapper"
                onClick={() => document.getElementById('qr-upload')?.click()}
              >
                {refundQr ? (
                  <p>{refundQr.name}</p>
                ) : (
                  <>
                    <p style={{ fontSize: '24px', marginBottom: '10px' }}>📷</p>
                    <p>Click to upload QR Code Image</p>
                  </>
                )}
                <input
                  type="file"
                  id="qr-upload"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setRefundQr(e.target.files[0]);
                    }
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundQr(null);
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!refundQr || uploading}
                onClick={async () => {
                  if (!selectedWarranty || !refundQr) return;
                  setUploading(true);
                  try {
                    const formData = new FormData();
                    formData.append('refundQrCode', refundQr);

                    await axios.post(`/api/warranty/${selectedWarranty}/refund-request`, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data'
                      }
                    });

                    alert('Refund request submitted successfully!');
                    setShowRefundModal(false);
                    setRefundQr(null);
                    fetchWarranties();
                  } catch (error: any) {
                    console.error('Refund request failed:', error);
                    alert(error.response?.data?.error || 'Failed to submit refund request');
                  } finally {
                    setUploading(false);
                  }
                }}
              >
                {uploading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )
      }
    </>
  );
};

export default CustomerDashboard;

